import { useState } from "react";
import { toast } from "sonner";
import platformApi from "@/lib/platformApi";
import { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { PasswordInput } from "@/components/ui/password-input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";
import { Plus, Loader2 } from "lucide-react";

const PLANS = [
  ["launch", "Launch — up to 5 employees"],
  ["micro", "Micro — up to 10 employees"],
  ["starter", "Starter — up to 15 employees"],
  ["team", "Team — up to 35 employees"],
  ["growth", "Growth — up to 45 employees"],
  ["business", "Business — up to 50 employees"],
  ["scale", "Scale — up to 200 employees"],
  ["corporate", "Corporate — up to 300 employees"],
  ["enterprise", "Enterprise — up to 500 employees"],
];

export const CreateTenantDialog = ({ onCreated }) => {
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    org_name: "", subdomain: "", plan: "starter",
    admin_email: "", admin_password: "", admin_name: "",
  });
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async () => {
    setSaving(true);
    try {
      const payload = { org_name: form.org_name, subdomain: form.subdomain, plan: form.plan };
      if (form.admin_email) {
        payload.admin_email = form.admin_email;
        payload.admin_password = form.admin_password;
        payload.admin_name = form.admin_name || undefined;
      }
      const { data } = await platformApi.post("/tenants", payload);
      toast.success(`Tenant '${data.subdomain}' created${data.admin_created ? " with initial admin" : ""}`);
      setOpen(false);
      setForm({ org_name: "", subdomain: "", plan: "starter", admin_email: "", admin_password: "", admin_name: "" });
      onCreated();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button data-testid="create-tenant-btn"
          className="rounded-none h-9 text-[11px] brand-accent-bg hover:bg-teal-700 text-white">
          <Plus className="h-3.5 w-3.5" /> New Tenant
        </Button>
      </DialogTrigger>
      <DialogContent className="rounded-none sm:max-w-md" data-testid="create-tenant-dialog">
        <DialogHeader>
          <DialogTitle className="text-[#1E293B]">Create Tenant</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label className="text-[11px] uppercase tracking-wider text-slate-500">Organization name</Label>
            <Input data-testid="tenant-org-input" value={form.org_name} onChange={set("org_name")}
              placeholder="ToothMatrix Dental Group" className="rounded-none mt-1" />
          </div>
          <div>
            <Label className="text-[11px] uppercase tracking-wider text-slate-500">Subdomain</Label>
            <Input data-testid="tenant-subdomain-input" value={form.subdomain} onChange={set("subdomain")}
              placeholder="toothmatrix" className="rounded-none mt-1 font-mono" />
            <p className="text-[10px] text-slate-400 mt-1 font-mono">{form.subdomain || "subdomain"}.aetriaos.com</p>
          </div>
          <div>
            <Label className="text-[11px] uppercase tracking-wider text-slate-500">Plan</Label>
            <select data-testid="tenant-plan-select" value={form.plan} onChange={set("plan")}
              className="mt-1 w-full h-9 border border-input bg-white px-3 text-sm">
              {PLANS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
            </select>
          </div>
          <div className="border-t border-slate-100 pt-3">
            <p className="text-[10px] font-semibold uppercase tracking-[0.12em] text-slate-400 mb-2">
              Initial admin (optional)
            </p>
            <div className="space-y-2">
              <Input data-testid="tenant-admin-email-input" value={form.admin_email} onChange={set("admin_email")}
                placeholder="admin@org.com" className="rounded-none" />
              <PasswordInput data-testid="tenant-admin-password-input" value={form.admin_password}
                onChange={set("admin_password")} placeholder="Password (min 8 chars)" className="rounded-none" />
              <Input data-testid="tenant-admin-name-input" value={form.admin_name} onChange={set("admin_name")}
                placeholder="Admin display name" className="rounded-none" />
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button data-testid="create-tenant-submit" onClick={submit}
            disabled={saving || !form.org_name || !form.subdomain}
            className="rounded-none bg-[#1E293B] hover:bg-slate-700 text-white">
            {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : null} Create tenant
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default CreateTenantDialog;
